import { Link, Navigate, useNavigate } from 'react-router-dom';
import { BarChart3, LogOut, Zap, Gamepad2, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { BrandHeader } from '@/components/layout/BrandHeader';
import { useCurrentPlayer, useStore } from '@/app/store';
import { useGoogleAuth } from '@/features/auth/useGoogleAuth';
import type { GameId } from '@/types/player.types';

import '@/styles/page-shell.css';
import '@/styles/menu.css';

interface GameCard {
  id: GameId;
  num: number;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  maxPts: string;
  colorClass: 'c-amber' | 'c-blue' | 'c-green' | 'c-red';
}

const GAMES: GameCard[] = [
  { id: 'g1', num: 1, Icon: Zap,      title: 'SPEED DATA',         subtitle: '5 preguntas · 30s c/u', maxPts: '9.000', colorClass: 'c-amber' },
  { id: 'g4', num: 2, Icon: Gamepad2, title: 'POKÉMON / BIGDATA',  subtitle: '5 rondas · 10s c/u',    maxPts: '5.000', colorClass: 'c-green' },
  { id: 'g3', num: 3, Icon: Search,   title: 'CASO DESCIFRADO',    subtitle: '5 pistas progresivas',  maxPts: '6.000', colorClass: 'c-red'   },
];

const TOTAL_GAMES = GAMES.length;

export function MenuPage() {
  const navigate = useNavigate();
  const player = useCurrentPlayer();
  const showToast = useStore((s) => s.showToast);
  const { signOutNow } = useGoogleAuth();

  if (!player) return <Navigate to="/register" replace />;

  // Sólo cuentan los juegos activos (G1/G3/G4 — G2 retirado)
  const playedCount = GAMES.filter((g) => player.gPlayed[g.id]).length;
  const pendingCount = TOTAL_GAMES - playedCount;
  const initials = player.name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleSignOut = async () => {
    try {
      await signOutNow();
      showToast('Sesión cerrada.', 'info');
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="page-shell">
      <div className="bg-grid" />
      <div className="bg-glow glow-top" />
      <div className="bg-glow glow-left" />

      <div className="wrap">
        {/* Topbar */}
        <div className="topbar">
          <BrandHeader />
          <div className="menu-total">
            <span className="menu-total-label">TOTAL</span>
            <span className="menu-total-value">{player.scores.total.toLocaleString()}</span>
          </div>
        </div>

        {/* Player info (izquierda) + acciones (derecha) */}
        <div className="menu-player-row">
          <div className="menu-player">
            <div className="menu-player-avatar">{initials || '?'}</div>
            <div className="menu-player-info">
              <p className="menu-player-label">JUGANDO COMO</p>
              <p className="menu-player-name">{player.name}</p>
              <p className="menu-player-company">{player.company} · {player.position}</p>
            </div>
          </div>
          <div className="menu-actions menu-actions-top">
            <Link to="/leaderboard" className="btn btn-primary">
              <BarChart3 className="w-[16px] h-[16px]" /> VER RANKING
            </Link>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleSignOut}
              title="Cerrar sesión de Google"
            >
              <LogOut className="w-[16px] h-[16px]" /> CERRAR SESIÓN
            </button>
          </div>
        </div>

        {/* Progress */}
        <section className="menu-progress" aria-label="Progreso">
          <div className="menu-progress-stat">
            <p className="menu-progress-label">TOTAL JUEGOS</p>
            <p className="menu-progress-num">{TOTAL_GAMES}</p>
          </div>
          <div className="menu-progress-stat played">
            <p className="menu-progress-label">JUGADOS</p>
            <p className="menu-progress-num">{playedCount}</p>
          </div>
          <div className="menu-progress-stat pending">
            <p className="menu-progress-label">PENDIENTES</p>
            <p className="menu-progress-num">{pendingCount}</p>
          </div>
        </section>

        {/* Section header */}
        <div className="section-head">
          <span className="section-bar" />
          <h2 className="section-title">LOS {TOTAL_GAMES} DESAFÍOS</h2>
          <div className="section-line" />
          <span className="section-tag">
            {playedCount === TOTAL_GAMES ? '¡COMPLETOS!' : `${pendingCount} POR JUGAR`}
          </span>
        </div>

        {/* Cards — los ya jugados se renderizan como <div> no clickable */}
        <div className="menu-cards">
          {GAMES.map((g) => {
            const { Icon } = g;
            const played = player.gPlayed[g.id];
            const score = player.scores[g.id] ?? 0;
            const className = `menu-card ${g.colorClass} ${played ? 'played' : ''}`;
            const content = (
              <>
                <div className="menu-card-top">
                  <div className="menu-card-ico">
                    <Icon className="w-7 h-7" />
                  </div>
                  <span className="menu-card-idx">0{g.num}</span>
                </div>
                <h3 className="menu-card-name">{g.title}</h3>
                <p className="menu-card-desc">{g.subtitle}</p>
                <div className="menu-card-foot">
                  <span className="menu-card-max">MAX {g.maxPts}</span>
                  <span className="menu-card-status">
                    {played ? `✓ ${score.toLocaleString()}` : 'JUGAR →'}
                  </span>
                </div>
              </>
            );
            return played ? (
              <div
                key={g.id}
                className={className}
                aria-disabled="true"
                title="Ya jugaste este desafío"
              >
                {content}
              </div>
            ) : (
              <Link key={g.id} to={`/game/${g.id}`} className={className}>
                {content}
              </Link>
            );
          })}
        </div>

      </div>
    </div>
  );
}
